import axios from 'axios';

export interface TopicPosition {
  start: number; // word index relative to sentence
  end: number;   // word index relative to sentence
  topic: string;
  sentenceIndex: number; // which sentence this position belongs to
}

export interface ClassificationResult {
  sentenceIndex: number;
  topicPositions: TopicPosition[];
}

class ClassificationAPI {
  private static readonly API_URL = 'https://realtime-classification.culturex.com/classify';

  static async classifyText(text: string): Promise<ClassificationResult[]> {
    const response = await axios.post<ClassificationResult[]>(
      this.API_URL,
      { text },
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    

    // Parse the nested structure into a more usable format
    return response.data.map((item) => {
      const topicPositions: TopicPosition[] = [];

      // Parse topicPositions array structure:
      // Each positionGroup has format: [[[start1, end1], [start2, end2], ...], [["topic1", "topic2", ...]]]
      // The intervals are word positions (0-indexed), inclusive on both ends
      // Example structure:
      //   positionGroup[0] = [[22, 22]]  (single interval)
      //   positionGroup[0] = [[10, 10], [11, 11]]  (multiple intervals)
      //   positionGroup[1] = [["topic name"]] or [["topic1", "topic2"]]
      if (item.topicPositions && Array.isArray(item.topicPositions)) {
        item.topicPositions.forEach((positionGroup: any) => {
          if (positionGroup && Array.isArray(positionGroup) && positionGroup.length >= 2) {
            // positionGroup[0] = array of intervals: [[22, 22]] or [[10, 10], [11, 11]]
            const intervals = positionGroup[0];
            
            // positionGroup[1] = [["topic name"]] or [["topic1", "topic2", ...]]
            // positionGroup[1][0] = ["topic name"] or ["topic1", "topic2", ...]
            const topicNames = positionGroup[1]?.[0];

            if (intervals && Array.isArray(intervals) && topicNames && Array.isArray(topicNames) && topicNames.length > 0) {
              // Combine all topic names (remove duplicates and join with " / ")
              const uniqueTopics = Array.from(new Set(topicNames.filter((t: any) => t && typeof t === 'string')));
              const topicName = uniqueTopics.join(' / ');
              
              // Each element in intervals is an interval [start, end]
              // intervals = [[22, 22]] means one interval [22, 22]
              // intervals = [[10, 10], [11, 11]] means two intervals
              intervals.forEach((interval: any) => {
                if (Array.isArray(interval) && interval.length >= 2) {
                  // interval should be [start, end] like [22, 22] or [10, 10]
                  const start = interval[0];
                  const end = interval[1];
                  
                  if (typeof start === 'number' && typeof end === 'number') {
                    topicPositions.push({
                      start: start, // word index (0-based, relative to sentence)
                      end: end,     // word index (0-based, inclusive, relative to sentence)
                      topic: topicName,
                      sentenceIndex: item.sentenceIndex,
                    });
                  }
                }
              });
            }
          }
        });
      }

      return {
        sentenceIndex: item.sentenceIndex,
        topicPositions,
      };
    });
  }
}

export default ClassificationAPI;
